import { useEffect } from 'react';
import globalWindow from '@shared/core/globals';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

/**
 * Refreshes the picker's roster each time it opens, and each time the tab comes back into view while
 * it is open. The picker's Edit Guest List opens the guest list in another tab, and nothing else
 * would carry edits made there into this tab's cache (MSG-568).
 *
 * `refresh` should keep the current roster on screen while it runs and handle its own failures: a
 * background refresh that fails leaves the list as it was rather than replacing it with an error.
 */
export const useRefreshWhileOpen = (isOpen: boolean, refresh: () => void) => {
  const refreshLatest = useEventCallback(refresh);

  useEffect(() => {
    const doc = globalWindow.document;
    if (!isOpen || !doc) return;
    refreshLatest();
    const handleVisibilityChange = () => {
      if (doc.visibilityState === 'visible') refreshLatest();
    };
    doc.addEventListener('visibilitychange', handleVisibilityChange);
    return () => doc.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isOpen, refreshLatest]);
};
